import {skipToken, useQuery} from '@github-ui/react-query'
import type {FieldDefinition} from '../../saved-view-options/fields'
import type {SavedViewsBoardDeferredFieldsSource} from '../types'
import type {BoardPayloadItem} from './board-payload'
import type {BoardDeferredFields} from './deferred-payload'

export const DEFERRED_CARD_FIELDS_QUERY_PREFIX = 'saved-views-board-deferred'
const DEFERRED_CARD_FIELDS_STALE_TIME_MS = 60_000

/**
 * Loads fields omitted from the initial board payload. Values are cached by view and item ID.
 */
export type DeferredCardFieldsSource = SavedViewsBoardDeferredFieldsSource<BoardDeferredFields>

/**
 * The deferred fields of one card, asked for only when the card shows one of them. A board
 * draws only the cards in view, so a card asking on mount asks for what is on screen; the
 * host's `load` batches those into one request.
 *
 * `undefined` until they arrive, and always without a source.
 */
export function useDeferredCardFields(
  item: BoardPayloadItem,
  visibleFieldIdentifiers: ReadonlySet<string>,
  fieldDefinitions: readonly FieldDefinition[] = [],
  source?: DeferredCardFieldsSource,
): BoardDeferredFields | undefined {
  const wanted = fieldDefinitions.some(
    ({identifier, deferred, capabilities}) =>
      deferred === true &&
      visibleFieldIdentifiers.has(identifier) &&
      (capabilities.attributable ?? []).includes(item.type),
  )

  const {data} = useQuery({
    queryKey: [DEFERRED_CARD_FIELDS_QUERY_PREFIX, ...(source?.scope ?? []), item.globalId],
    queryFn:
      source && wanted ? async (): Promise<BoardDeferredFields> => (await source.load(item.globalId)) ?? {} : skipToken,
    // Cached values draw at once; once stale, a card drawn again refreshes them behind the scenes.
    staleTime: DEFERRED_CARD_FIELDS_STALE_TIME_MS,
  })

  return wanted ? data : undefined
}
